import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Calendar, X, Check, Users, Clock, PlusCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateOneOnOneSession } from "@/hooks/api-hooks";
import { generateConductUrl } from "@/lib/utils";

const createOneOnOneSessionSchema = z.object({
  participantId: z.string().min(1, "Please select a participant"),
  managerId: z.string().min(1, "Please select a manager"),
  scheduledDate: z.string().min(1, "Please select a date and time"),
  duration: z.number().min(15, "Duration must be at least 15 minutes").max(180, "Duration cannot exceed 180 minutes"),
  agenda: z.array(z.string()).default([]),
  setupId: z.string().optional(),
});

type CreateOneOnOneSessionFormData = z.infer<typeof createOneOnOneSessionSchema>;

interface OneOnOneSetup {
  id: string;
  title?: string;
  participantId: string;
  managerId: string;
  frequency: string;
  duration: number;
  defaultAgenda: string[];
  isActive: boolean;
}

interface TeamMember {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  role: 'owner' | 'admin' | 'manager' | 'staff';
}

interface CreateOneOnOneSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamMembers: TeamMember[];
  oneOnOneSetups: OneOnOneSetup[];
  preselectedSetup?: OneOnOneSetup;
  onSuccess?: (session?: any) => void;
}

export function CreateOneOnOneSessionModal({
  isOpen,
  onClose,
  teamId,
  teamMembers,
  oneOnOneSetups,
  preselectedSetup,
  onSuccess
}: CreateOneOnOneSessionModalProps) {
  const createSessionMutation = useCreateOneOnOneSession();
  const [agendaInput, setAgendaInput] = React.useState("");

  const form = useForm<CreateOneOnOneSessionFormData>({
    resolver: zodResolver(createOneOnOneSessionSchema),
    defaultValues: {
      participantId: preselectedSetup?.participantId || "",
      managerId: preselectedSetup?.managerId || "",
      scheduledDate: "",
      duration: preselectedSetup?.duration || 30,
      agenda: preselectedSetup?.defaultAgenda ? [...preselectedSetup.defaultAgenda] : [],
      setupId: preselectedSetup?.id || "",
    },
  });

  const watchedAgenda = form.watch("agenda");
  const watchedSetupId = form.watch("setupId");

  // Update form when a setup is selected
  React.useEffect(() => {
    if (watchedSetupId) {
      const setup = oneOnOneSetups.find(s => s.id === watchedSetupId);
      if (setup) {
        form.setValue("participantId", setup.participantId);
        form.setValue("managerId", setup.managerId);
        form.setValue("duration", setup.duration);
        form.setValue("agenda", [...setup.defaultAgenda]);
      }
    }
  }, [watchedSetupId, oneOnOneSetups, form]);

  // Reset form when preselectedSetup changes
  React.useEffect(() => {
    if (preselectedSetup) {
      form.reset({
        participantId: preselectedSetup.participantId,
        managerId: preselectedSetup.managerId,
        scheduledDate: "",
        duration: preselectedSetup.duration,
        agenda: [...preselectedSetup.defaultAgenda],
        setupId: preselectedSetup.id,
      });
    }
  }, [preselectedSetup, form]);

  const handleAddAgendaItem = () => {
    if (agendaInput.trim()) {
      const currentAgenda = form.getValues("agenda");
      form.setValue("agenda", [...currentAgenda, agendaInput.trim()]);
      setAgendaInput("");
    }
  };

  const handleRemoveAgendaItem = (index: number) => {
    const currentAgenda = form.getValues("agenda");
    const newAgenda = currentAgenda.filter((_, i) => i !== index);
    form.setValue("agenda", newAgenda);
  };

  const onSubmit = async (data: CreateOneOnOneSessionFormData) => {
    createSessionMutation.mutate(
      { teamId, ...data },
      {
        onSuccess: (sessionResult) => {
          const participant = teamMembers.find(m => m.user.id === data.participantId);
          const manager = teamMembers.find(m => m.user.id === data.managerId);
          toast.success(`1-on-1 session scheduled for ${participant?.user.firstName} & ${manager?.user.firstName}!`);

          if (sessionResult) {
            const conductUrl = generateConductUrl({
              ...sessionResult,
              type: sessionResult.deliveryMethod === 'email' ? 'Email' : 'Physical Meeting',
            });
            window.open(conductUrl, '_blank', 'noopener');
          }

          onSuccess?.(sessionResult);
          onClose();
          form.reset();
          setAgendaInput("");
        },
        onError: (error: any) => {
          console.error("Error creating 1-on-1 session:", error);
          toast.error(`Failed to schedule session: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    onClose();
    form.reset();
    setAgendaInput("");
  };

  const normalizeRole = (role: string) => role?.toLowerCase?.() ?? '';

  const managers = teamMembers.filter(member => {
    const role = normalizeRole(member.role);
    return ['owner', 'admin', 'manager'].includes(role);
  });

  const staff = teamMembers.filter(member => {
    const role = normalizeRole(member.role);
    return role === 'staff' || role === 'member' || role === 'team_lead';
  });

  const allMembers = [...managers, ...staff];

  // Get minimum date (today)
  const today = new Date();
  const minDateTime = today.toISOString().slice(0, 16);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-4 w-4 text-green-600" />
            </div>
            Schedule 1-on-1 Session
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {!preselectedSetup && (
              <FormField
                control={form.control}
                name="setupId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Use Existing Setup (Optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a setup to auto-fill details" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {oneOnOneSetups.map((setup) => {
                          const participant = teamMembers.find(m => m.user.id === setup.participantId);
                          const manager = teamMembers.find(m => m.user.id === setup.managerId);
                          const title = setup.title && setup.title.trim().length > 0 ? setup.title : '1-on-1 Session';
                          return (
                            <SelectItem key={setup.id} value={setup.id}>
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-foreground">{title}</span>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>
                                    {participant?.user.firstName} & {manager?.user.firstName}
                                  </span>
                                  <Badge variant="secondary" className="text-xs uppercase tracking-wide">
                                    {setup.frequency}
                                  </Badge>
                                </div>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="participantId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Participant
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select participant" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {allMembers.map((member) => (
                          <SelectItem key={member.user.id} value={member.user.id}>
                            <div className="flex items-center gap-2">
                              <span>{member.user.firstName} {member.user.lastName}</span>
                              <Badge variant={member.role === 'staff' ? 'secondary' : 'default'} className="text-xs">
                                {member.role}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="managerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Manager
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select manager" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {allMembers.map((member) => (
                          <SelectItem key={member.user.id} value={member.user.id}>
                            <div className="flex items-center gap-2">
                              <span>{member.user.firstName} {member.user.lastName}</span>
                              <Badge variant={['owner', 'admin', 'manager'].includes(member.role) ? 'default' : 'secondary'} className="text-xs">
                                {member.role}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="scheduledDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date & Time
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        min={minDateTime}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Duration (minutes)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={15}
                        max={180}
                        step={5}
                        placeholder="30"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Agenda Section */}
            <div className="space-y-4">
              <FormLabel>Agenda Items</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="Add agenda item..."
                  value={agendaInput}
                  onChange={(e) => setAgendaInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAgendaItem();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddAgendaItem}
                  disabled={!agendaInput.trim()}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
              </div>

              {watchedAgenda.length > 0 && (
                <div className="space-y-2">
                  {watchedAgenda.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                      <span className="flex-1 text-sm">{item}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAgendaItem(index)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={createSessionMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createSessionMutation.isPending}
                className="gap-2"
              >
                {createSessionMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-border border-t-foreground"></div>
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Schedule Session
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
